import { addDays, fromISODate } from '../lib/dates';
import { parseQuickAdd } from '../tasks/parseQuickAdd';

// One line, four things it can be: "pizza 12 contanti ieri" (a movement), "dentista domani 15"
// (an event), "studio 2 ore" (a block of time today), anything else (a task, as quick add reads it).

export type Intent =
  | {
      kind: 'transaction';
      amount: number;
      note: string;
      day: string;
      method: 'cash' | 'card' | 'transfer' | null;
    }
  | { kind: 'event'; title: string; day: string; time: string; minutes: number }
  | { kind: 'block'; title: string; minutes: number }
  | { kind: 'task'; title: string; dueDate: string | null; priority: number };

const normalize = (word: string) => word.toLowerCase().normalize('NFD').replace(/\p{M}/gu, '');

const WEEKDAYS: Record<string, number> = {
  lunedi: 1,
  martedi: 2,
  mercoledi: 3,
  giovedi: 4,
  venerdi: 5,
  sabato: 6,
  domenica: 0,
};

const METHODS: Record<string, 'cash' | 'card' | 'transfer'> = {
  contanti: 'cash',
  cash: 'cash',
  carta: 'card',
  bancomat: 'card',
  pos: 'card',
  bonifico: 'transfer',
};

/** Words that say "money came in". */
const INCOME = new Set(['entrata', 'incasso', 'incassato', 'stipendio', 'rimborso', 'ricevuto']);
const FILLER = new Set(['euro', 'eur', '€', 'alle', 'ore', 'il', 'di', 'per', 'con', 'in']);

/** A day word: for money it is the past (ieri, lunedì scorso), for events the future. */
function dayWord(key: string, reference: string, past: boolean): string | null {
  if (key === 'oggi') return reference;
  if (key === 'ieri') return addDays(reference, -1);
  if (key === 'altroieri' || key === "l'altroieri") return addDays(reference, -2);
  if (key === 'domani') return addDays(reference, 1);
  if (key === 'dopodomani') return addDays(reference, 2);
  if (key in WEEKDAYS) {
    const current = fromISODate(reference).getDay();
    const target = WEEKDAYS[key] ?? 0;
    const delta = past ? -((current - target + 7) % 7) : (target - current + 7) % 7 || 7;
    return addDays(reference, delta);
  }
  return null;
}

const AMOUNT = /^[+-]?\d+(?:[.,]\d{1,2})?€?$/;
const TIME = /^(?:h)?([01]?\d|2[0-3])(?:[:.]([0-5]\d))?$/;

/** What the line most likely is, and the other ways it can be read (Tab switches). */
export function parseCommand(input: string, reference: string): Intent[] {
  const text = input.trim();
  if (!text) return [];
  const words = text.split(/\s+/);
  const keys = words.map(normalize);

  // "2 ore", "90 min", "1h": a length of time.
  const durationAt = keys.findIndex((key, index) => {
    const next = keys[index + 1] ?? '';
    return (
      (/^\d+(?:[.,]\d)?$/.test(key) && /^(ore|ora|h|min|minuti)$/.test(next)) ||
      /^\d+(?:[.,]\d)?(h|min)$/.test(key)
    );
  });
  const hasMoneyCue = keys.some(
    (key) =>
      key in METHODS ||
      INCOME.has(key) ||
      key === 'euro' ||
      key.includes('€') ||
      /[.,]\d{1,2}$/.test(key),
  );
  const dayIndex = keys.findIndex((key) => dayWord(key, reference, false) !== null);
  const timeCue = keys.some((key) => key === 'alle' || key === 'ore');

  const intents: Intent[] = [];
  const rest = (skip: Set<number>) =>
    words.filter((_, index) => !skip.has(index) && !FILLER.has(keys[index] ?? '')).join(' ');

  // A block of time: "studio 2 ore".
  if (durationAt >= 0) {
    const key = keys[durationAt] ?? '';
    const number = Number.parseFloat(key.replace(',', '.'));
    const unit = /min/.test(key) || /^min/.test(keys[durationAt + 1] ?? '') ? 1 : 60;
    const skip = new Set([
      durationAt,
      /^\d/.test(keys[durationAt + 1] ?? 'x') ? -1 : durationAt + 1,
    ]);
    const minutes = Math.round(number * unit);
    if (minutes >= 5 && minutes <= 600) {
      intents.push({ kind: 'block', title: rest(skip) || 'Focus', minutes });
    }
  }

  // An amount: the first number that is not a time of day.
  const amountAt = keys.findIndex((key) => AMOUNT.test(key) && !TIME.test(key.replace(/€$/, '')));
  const plainNumberAt = keys.findIndex((key) => /^\d+$/.test(key));
  const moneyAt = amountAt >= 0 ? amountAt : plainNumberAt;
  const money = (() => {
    if (moneyAt < 0 || durationAt === moneyAt) return null;
    const raw = keys[moneyAt] ?? '';
    const cents = Math.round(Number.parseFloat(raw.replace('€', '').replace(',', '.')) * 100);
    if (!Number.isFinite(cents) || cents === 0) return null;
    let day = reference;
    let method: 'cash' | 'card' | 'transfer' | null = null;
    let income = raw.startsWith('+');
    const skip = new Set([moneyAt]);
    keys.forEach((key, index) => {
      const date = dayWord(key, reference, true);
      if (date) {
        day = date;
        skip.add(index);
      } else if (key in METHODS) {
        method = METHODS[key] ?? null;
        skip.add(index);
      } else if (INCOME.has(key)) {
        income = true;
      }
    });
    return {
      kind: 'transaction' as const,
      amount: income ? Math.abs(cents) : -Math.abs(cents),
      note: rest(skip),
      day,
      method,
    };
  })();

  // An event: a day and a time ("dentista domani 15", "cena venerdì alle 20:30").
  const event = (() => {
    const timeAt = keys.findIndex(
      (key, index) => TIME.test(key) && (timeCue || dayIndex >= 0 || keys[index - 1] === 'alle'),
    );
    if (timeAt < 0 || (dayIndex < 0 && !timeCue)) return null;
    const match = (keys[timeAt] ?? '').match(TIME);
    const time = `${String(match?.[1] ?? '9').padStart(2, '0')}:${match?.[2] ?? '00'}`;
    const day =
      dayIndex >= 0 ? (dayWord(keys[dayIndex] ?? '', reference, false) ?? reference) : reference;
    const skip = new Set([timeAt, dayIndex]);
    return { kind: 'event' as const, title: rest(skip) || 'Evento', day, time, minutes: 60 };
  })();

  // Which first: money cues win, then a day with a time, then a plain amount.
  if (money && hasMoneyCue) intents.unshift(money);
  if (event) intents.push(event);
  if (money && !hasMoneyCue) intents.push(money);
  const task = parseQuickAdd(text, reference);
  intents.push({
    kind: 'task',
    title: task.title || text,
    dueDate: task.dueDate,
    priority: task.priority,
  });

  // The most specific reading first: a block if asked for, an event before a bare amount.
  const order = (intent: Intent) =>
    intent.kind === 'transaction' && hasMoneyCue
      ? 0
      : intent.kind === 'block'
        ? 1
        : intent.kind === 'event'
          ? 2
          : intent.kind === 'transaction'
            ? 3
            : 4;
  return intents.sort((a, b) => order(a) - order(b));
}

/** Text without accents, lowercase, for searching. */
export const searchKey = normalize;
